import React, { Suspense, useCallback, useMemo } from 'react';
import { View, Image, useWindowDimensions, ScrollView } from 'react-native';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { ArticleCard } from '~global-components';
import TagsiteBeautyShortsOverlay from './TagsiteBeautyShortsOverlay';

import {
  CreateResponsiveStyle,
  isWeb,
  screenWidth,
  maxWidthContainer,
} from '~global-screens';
import { vs } from '@web-styles';
import {
  capitalizeLetter,
  capitalizeFirstLetter,
  isIncludesHttp,
} from '@web-utils';
import {
  selectTagsiteBeautyShorts,
  selectTagsiteBeautyShortsLoading,
} from '~store/tagsite/tagsiteSlice';

const TagsiteInfiniteListSection = ({ post_type, content, tagsite }) => {
  const styles = componentStyles();
  const { width } = useWindowDimensions();
  const beautyShorts = useSelector(selectTagsiteBeautyShorts);
  const beautyShortsLoading = useSelector(selectTagsiteBeautyShortsLoading);

  const shortsEnabled = tagsite?.shorts_bg_enabled ?? false;

  const numColumns = isWeb ? 4 : 2;

  const processedData = useMemo(() => {
    if (!content || !content.length) {
      return { regular: [], banner: null };
    }

    // Only consider the background image if shorts_bg_enabled is true
    const hasTagsiteImage =
      shortsEnabled &&
      (tagsite?.shorts_desktop_bg_url || tagsite?.shorts_mobile_bg_url);

    const banner = hasTagsiteImage
      ? {
          _id: 'tagsite-image-banner',
          isTagsiteImage: true,
          desktopImage: tagsite.shorts_desktop_bg_url,
          mobileImage: tagsite.shorts_mobile_bg_url,
        }
      : null;

    return { regular: content, banner };
  }, [content, tagsite, shortsEnabled]);

  // Skip adding banner to display data if beauty shorts are available
  const displayDataWeb = useMemo(() => {
    if (!processedData.regular.length) {
      return [];
    }

    const result = [...processedData.regular];

    if (
      processedData.banner &&
      (beautyShortsLoading || !beautyShorts?.length)
    ) {
      result.splice(numColumns, 0, processedData.banner);
    }

    return result;
  }, [processedData, numColumns, beautyShorts, beautyShortsLoading]);

  const groupedWebItems = useMemo(() => {
    const result = [];

    let bannerIndex = -1;

    if (!displayDataWeb) {
      return {
        rows: [],
        bannerIndex,
      };
    }

    const items = [...displayDataWeb];

    let currentRow = [];

    items.forEach((item, index) => {
      if (item.isTagsiteImage) {
        if (currentRow.length > 0) {
          result.push(currentRow);
          currentRow = [];
        }

        result.push([item]);
        bannerIndex = result.length - 1;
      } else {
        currentRow.push(item);

        if (currentRow.length === numColumns) {
          result.push(currentRow);
          currentRow = [];
        }
      }
    });

    if (currentRow.length > 0) {
      result.push(currentRow);
    }

    return {
      rows: result,
      bannerIndex,
    };
  }, [displayDataWeb, numColumns]);

  const renderWebItem = (item, index, isLastInRow, totalInRow) => {
    if (item.isTagsiteImage) {
      const imageSource = item.desktopImage || item.mobileImage;
      if (!imageSource) {
        return null;
      }

      return (
        <View
          key={item._id}
          style={styles('tagsiteBannerContainer')}
          nativeID="tagsite-banner-image">
          <Image
            source={{ uri: imageSource }}
            style={styles('tagsiteBannerImage')}
            resizeMode="cover"
          />
        </View>
      );
    }

    const { slug, category, title, owner, attachments } = item || {};

    // Calculate width with even spacing based on items in the row
    const itemWidth = `${100 / numColumns - 2}%`;

    return (
      <View
        key={item._id}
        style={[
          styles('webItemContainer'),
          { width: itemWidth },
          isLastInRow ? styles('lastInRow') : null,
        ]}
        nativeID={`tagsite-article-${index}`}>
        <ArticleCard
          noBorder
          noDate={true}
          imageWidth={(width / numColumns) * 0.6}
          containerWidth="100%"
          imageToTextWidth={16}
          textToTextWidth={14}
          metaFonts={[styles('metaFonts')]}
          titleFonts={[styles('titleFonts'), { color: tagsite?.color_theme }]}
          authorFonts={[styles('authorFonts')]}
          post_type={post_type}
          slug={slug}
          data={{
            category: capitalizeLetter(category?.name),
            categorySlug: category?.slug,
            title: title,
            author: capitalizeFirstLetter(owner?.name),
            authorUserName: owner?.user_name,
            date: undefined, // Explicitly remove date
            imageSource: {
              uri: isIncludesHttp(attachments?.[0]?.url)
                ? attachments?.[0]?.url
                : `https://img.youtube.com/vi/${attachments?.[0]?.url}/sddefault.jpg`,
            },
          }}
        />
      </View>
    );
  };

  // Render function for mobile regular items
  const renderMobileItem = useCallback(
    ({ item, index }) => {
      // Mobile articles
      const { slug, category, title, owner, attachments } = item || {};

      return (
        <View
          style={styles('mobileItemFlex')}
          nativeID={`tagsite-article-mobile-${index}`}>
          <ArticleCard
            noBorder
            noDate={true}
            imageWidth={165}
            containerWidth="100%"
            imageToTextWidth={vs(10)}
            textToTextWidth={vs(10)}
            metaFonts={[styles('metaFonts')]}
            titleFonts={[styles('titleFonts'), { color: tagsite?.color_theme }]}
            authorFonts={[styles('authorFonts')]}
            post_type={post_type}
            slug={slug}
            data={{
              category: capitalizeLetter(category?.name),
              categorySlug: category?.slug,
              title: title,
              author: capitalizeFirstLetter(owner?.name),
              authorUserName: owner?.user_name,
              date: undefined,
              imageSource: {
                uri: isIncludesHttp(attachments?.[0]?.url)
                  ? attachments?.[0]?.url
                  : `https://img.youtube.com/vi/${attachments?.[0]?.url}/sddefault.jpg`,
              },
            }}
          />
        </View>
      );
    },
    [styles, post_type, tagsite],
  );

  // Render the mobile banner separately from FlatList
  const renderMobileBanner = () => {
    if (!shortsEnabled) {
      return null;
    }

    if (beautyShorts?.length && !beautyShortsLoading) {
      return null;
    }

    if (!processedData.banner) {
      return null;
    }

    const imageSource =
      processedData.banner.mobileImage || processedData.banner.desktopImage;
    if (!imageSource) {
      return null;
    }

    return (
      <View
        style={styles('tagsiteImageContainer')}
        nativeID="tagsite-banner-image-mobile">
        <Image
          source={{ uri: imageSource }}
          style={styles('tagsiteImage')}
          resizeMode="cover"
        />
      </View>
    );
  };

  const beautyShortsBackgroundImage = useMemo(() => {
    if (!shortsEnabled || !processedData.banner) {
      return null;
    }
    return isWeb
      ? processedData.banner.desktopImage
      : processedData.banner.mobileImage;
  }, [processedData.banner, isWeb, shortsEnabled]);

  return (
    <Suspense fallback={<></>}>
      <View
        style={styles('container')}
        nativeID="tagsite-infinite-list-container">
        {isWeb ? (
          // Web layout
          <View style={styles('webContainer')}>
            {/* First row of articles */}
            {groupedWebItems.rows.length > 0 &&
              groupedWebItems.bannerIndex !== 0 && (
                <View key="first-row" style={styles('centeredRow')}>
                  <View style={styles('rowContent')}>
                    {groupedWebItems.rows[0].map((item, itemIndex) =>
                      renderWebItem(
                        item,
                        itemIndex,
                        itemIndex === groupedWebItems.rows[0].length - 1,
                        groupedWebItems.rows[0].length,
                      ),
                    )}
                  </View>
                </View>
              )}

            {/* Beauty Shorts Overlay (replaces tagsite banner) */}
            {shortsEnabled &&
              beautyShorts?.length > 0 &&
              !beautyShortsLoading && (
                <TagsiteBeautyShortsOverlay
                  beautyShorts={beautyShorts}
                  backgroundImage={beautyShortsBackgroundImage}
                />
              )}

            {/* Banner (if no beauty shorts) */}
            {shortsEnabled &&
              groupedWebItems.bannerIndex !== -1 &&
              (beautyShortsLoading || !beautyShorts?.length) &&
              groupedWebItems.rows[groupedWebItems.bannerIndex][0]
                .isTagsiteImage && (
                <View
                  key={`row-${groupedWebItems.bannerIndex}`}
                  style={styles('fullWidthRow')}>
                  {renderWebItem(
                    groupedWebItems.rows[groupedWebItems.bannerIndex][0],
                    0,
                    true,
                    1,
                  )}
                </View>
              )}

            {/* Remaining rows of articles */}
            {groupedWebItems.rows.map((row, rowIndex) => {
              // Skip the first row and banner row which are already rendered
              if (rowIndex === 0 || rowIndex === groupedWebItems.bannerIndex) {
                return null;
              }

              return (
                <View key={`row-${rowIndex}`} style={styles('centeredRow')}>
                  <View style={styles('rowContent')}>
                    {row.map((item, itemIndex) =>
                      renderWebItem(
                        item,
                        itemIndex,
                        itemIndex === row.length - 1,
                        row.length,
                      ),
                    )}
                  </View>
                </View>
              );
            })}
          </View>
        ) : (
          // Mobile layout
          <ScrollView>
            {/* First row of articles */}
            <View style={styles('mobileGrid')}>
              {processedData.regular.slice(0, 2).map((item, index) => (
                <React.Fragment key={item._id}>
                  <View style={styles('mobileItemWrapper')}>
                    {renderMobileItem({ item, index })}
                  </View>
                  {index === 0 && processedData.regular.length > 1 && (
                    <View style={styles('verticalDividerWrapped')} />
                  )}
                </React.Fragment>
              ))}
            </View>

            {/* Second row of articles - posts 3-4 */}
            {processedData.regular.length > 2 && (
              <View style={styles('mobileRemainingItems')}>
                {processedData.regular.slice(2, 4).map((item, index) => {
                  return (
                    <React.Fragment key={item._id}>
                      <View style={styles('mobileItemWrapper')}>
                        {renderMobileItem({ item, index: index + 2 })}
                      </View>
                      <View style={styles('verticalDividerWrapped')} />
                    </React.Fragment>
                  );
                })}
              </View>
            )}

            {shortsEnabled &&
              beautyShorts?.length > 0 &&
              !beautyShortsLoading && (
                <TagsiteBeautyShortsOverlay
                  beautyShorts={beautyShorts}
                  backgroundImage={beautyShortsBackgroundImage}
                />
              )}

            {/* Banner (if no beauty shorts) */}
            {renderMobileBanner()}

            {processedData.regular.length > 4 && (
              <View style={styles('mobileRemainingItems')}>
                {processedData.regular.slice(4).map((item, index) => {
                  const isEven = index % 2 === 0;
                  return (
                    <React.Fragment key={item._id}>
                      <View style={styles('mobileItemWrapper')}>
                        {renderMobileItem({ item, index: index + 4 })}
                      </View>
                      {isEven &&
                        index < processedData.regular.slice(4).length - 1 && (
                          <View style={styles('verticalDividerWrapped')} />
                        )}
                    </React.Fragment>
                  );
                })}
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </Suspense>
  );
};

const webStyles = {
  container: {
    width: '100vw',
    alignSelf: 'center',
    paddingTop: 24,
  },
  webContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  centeredRow: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'center',
  },
  rowContent: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    width: screenWidth * 0.88,
    maxWidth: maxWidthContainer,
  },
  fullWidthRow: {
    width: '100%',
  },
  tagsiteBannerContainer: {
    width: '100%',
    marginBottom: 30,
  },
  tagsiteBannerImage: {
    width: '100%',
    aspectRatio: 1440 / 724,
    borderRadius: 8,
  },
  webItemContainer: {
    marginRight: 20,
    marginBottom: 30,
  },
  lastInRow: {
    marginRight: 0,
  },
  metaFonts: {
    fontFamily: 'Work Sans',
    fontSize: 12,
    letterSpacing: 0.4,
  },
  titleFonts: {
    fontFamily: 'Work Sans',
    fontSize: 20,
    lineHeight: '26px',
  },
  authorFonts: {
    fontFamily: 'Work Sans',
    fontSize: 12,
  },
  tagsiteImageContainer: {
    width: '100%',
    marginVertical: 30,
  },
  tagsiteImage: {
    aspectRatio: 1440 / 724,
    borderRadius: 8,
  },
};

const mobileStyles = {
  container: {
    paddingVertical: 0,
    paddingHorizontal: 0,
    paddingTop: 0,
  },
  metaFonts: {
    fontSize: 10,
  },
  titleFonts: {
    fontSize: 16,
    lineHeight: '18px',
  },
  authorFonts: {
    fontSize: 10,
  },
  tagsiteImageContainer: {
    width: '100%',
    marginVertical: 20,
    paddingHorizontal: 10,
  },
  tagsiteImage: {
    aspectRatio: 390 / 605,
    borderRadius: 4,
    width: '100%',
  },
  mobileGrid: {
    flexDirection: 'row',
    paddingHorizontal: 10,
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  mobileItemFlex: {
    flex: 1,
  },
  mobileRemainingItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 10,
  },
  mobileItemWrapper: {
    width: '50%',
    paddingHorizontal: 5,
    marginBottom: 20,
  },
  verticalDivider: {
    width: 1,
    backgroundColor: 'black',
    alignSelf: 'stretch',
    marginHorizontal: 5,
  },
  verticalDividerWrapped: {
    width: 1,
    backgroundColor: 'black',
    alignSelf: 'stretch',
    height: '100%',
    position: 'absolute',
    left: '50%',
    top: 0,
  },
};

const componentStyles = CreateResponsiveStyle(webStyles, mobileStyles);

TagsiteInfiniteListSection.propTypes = {
  post_type: PropTypes.string,
  content: PropTypes.array,
  tagsite: PropTypes.shape({
    shorts_desktop_bg_url: PropTypes.string,
    shorts_mobile_bg_url: PropTypes.string,
    color_theme: PropTypes.string,
    shorts_bg_enabled: PropTypes.bool,
  }),
};

export default TagsiteInfiniteListSection;
